import React, { useContext, useState } from 'react';
import { AuthContext } from '../shared/Context/auth-context';
import { useSessionState } from '../shared/hooks/session-state-hook';
import { sessionKey } from '../shared/util/session-storage';
import { useCareMutation } from './care-api';
import {
  activeMember,
  entryScope,
  localDateTime,
  metricInfo,
  validateCareEntry,
  careEntryPayload
} from './care-utils';

export function reviewersFor(record, type) {
  const doctors = (record.members || []).filter(
    (member) =>
      member.role === 'doctor' && activeMember(member) && member.scopes.includes(entryScope(type))
  );
  if (
    record.viewer.role !== 'patient' &&
    record.primaryDoctorId &&
    !doctors.some((member) => member.personId === record.primaryDoctorId)
  ) {
    doctors.push({ personId: record.primaryDoctorId, name: 'Primary doctor' });
  }
  return doctors;
}

const EntryComposer = ({
  record,
  initialType,
  onClose,
  onSaved,
  initialTitle = '',
  initialBody = '',
  relatedEntryId = ''
}) => {
  const auth = useContext(AuthContext);
  const role = record.viewer.role;
  const actorId =
    role === 'patient' ? auth.patientId : role === 'doctor' ? auth.doctorId : auth.pharmacyId;
  const patientId = record.patient.id;
  const [draft, setDraft, clearDraft, stored] = useSessionState(
    sessionKey(`${role}:${actorId}`, `care:${patientId}:compose:${initialType}:${relatedEntryId}`),
    () => ({
      type: initialType,
      title: initialTitle,
      body: initialBody,
      occurredAt: localDateTime(),
      metric: 'blood_pressure',
      value: '',
      secondaryValue: '',
      context: '',
      readingContext: '',
      reviewerId: record.primaryDoctorId || ''
    })
  );
  const [validation, setValidation] = useState(null);
  const { mutate, saving, error } = useCareMutation();
  const setField = (event) => setDraft({ ...draft, [event.target.name]: event.target.value });
  const doctors = reviewersFor(record, draft.type);
  const reviewerValid =
    !draft.reviewerId || doctors.some((doctor) => doctor.personId === draft.reviewerId);
  const submit = async (event) => {
    event.preventDefault();
    const problem = validateCareEntry(draft);
    if (problem || !reviewerValid) {
      setValidation(
        problem ||
          'The selected reviewer no longer has access. Choose another reviewer or save without sending.'
      );
      return;
    }
    setValidation(null);
    try {
      await mutate(`/patients/${patientId}/entries`, 'POST', {
        ...careEntryPayload(draft),
        ...(relatedEntryId ? { relatedEntryId } : {})
      });
      clearDraft();
      onSaved(
        draft.reviewerId
          ? 'Your update was saved and sent for review.'
          : 'Your update was saved. It has not been sent for review.'
      );
    } catch {}
  };
  return (
    <section className="care-panel care-compose" aria-labelledby="care-compose-title">
      <div className="care-panel-heading">
        <div>
          <p className="eyebrow">ADD TO THE TIMELINE</p>
          <h2 id="care-compose-title">
            {initialType === 'vital'
              ? 'Record a reading'
              : initialType === 'pharmacy'
                ? 'Add a pharmacy update'
                : 'Share an update'}
          </h2>
        </div>
        <button type="button" className="care-text-button" onClick={onClose} disabled={saving}>
          Back to timeline
        </button>
      </div>
      <form onSubmit={submit}>
        <fieldset disabled={saving}>
          <div className="care-form-grid">
            {initialType !== 'vital' && initialType !== 'pharmacy' && (
              <label>
                Update type
                <select name="type" value={draft.type} onChange={setField}>
                  {role === 'patient' && <option value="symptom">A change in how I feel</option>}
                  {role === 'patient' && <option value="question">A treatment question</option>}
                  <option value="event">A health event or consultation</option>
                </select>
              </label>
            )}
            {initialType === 'vital' && (
              <label>
                Measurement
                <select
                  name="metric"
                  value={draft.metric}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      metric: event.target.value,
                      value: '',
                      secondaryValue: '',
                      context: '',
                      readingContext: ''
                    })
                  }
                >
                  {Object.entries(metricInfo).map(([value, info]) => (
                    <option key={value} value={value}>
                      {info.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              {initialType === 'vital' ? 'When was it measured?' : 'When did it happen?'}
              <input
                name="occurredAt"
                type="datetime-local"
                required
                max={localDateTime()}
                value={draft.occurredAt}
                onChange={setField}
              />
            </label>
            {initialType === 'vital' ? (
              <>
                <label>
                  {draft.metric === 'blood_pressure'
                    ? 'Systolic (mmHg)'
                    : `${metricInfo[draft.metric].label} (${metricInfo[draft.metric].unit})`}
                  <input
                    name="value"
                    type="number"
                    step="any"
                    min="0.01"
                    max={draft.metric === 'spo2' ? '100' : undefined}
                    inputMode="decimal"
                    required
                    value={draft.value}
                    onChange={setField}
                  />
                </label>
                {draft.metric === 'blood_pressure' && (
                  <label>
                    Diastolic (mmHg)
                    <input
                      name="secondaryValue"
                      type="number"
                      step="any"
                      min="0.01"
                      inputMode="decimal"
                      required
                      value={draft.secondaryValue}
                      onChange={setField}
                    />
                  </label>
                )}
                {draft.metric === 'glucose' ? (
                  <label>
                    Reading context
                    <select name="context" required value={draft.context} onChange={setField}>
                      <option value="">Choose a context</option>
                      <option value="fasting">Fasting</option>
                      <option value="after-meal">After a meal</option>
                      <option value="random">Random</option>
                    </select>
                  </label>
                ) : (
                  <label>
                    Reading context (optional)
                    <input
                      name="readingContext"
                      maxLength={200}
                      value={draft.readingContext}
                      onChange={setField}
                      placeholder="For example: at home, seated, after rest"
                    />
                  </label>
                )}
              </>
            ) : (
              <label className="care-full-width">
                Title
                <input
                  name="title"
                  required
                  maxLength={160}
                  value={draft.title}
                  onChange={setField}
                  placeholder={
                    initialType === 'pharmacy'
                      ? 'For example: part of the prescription was supplied'
                      : 'A short description of your update'
                  }
                />
              </label>
            )}
            <label className="care-full-width">
              {initialType === 'vital' ? 'Notes (optional)' : 'Details'}
              <textarea
                name="body"
                rows={4}
                required={initialType !== 'vital'}
                maxLength={4000}
                value={draft.body}
                onChange={setField}
                placeholder={
                  initialType === 'vital'
                    ? 'Add anything that helps explain this reading.'
                    : 'Include when it started, what changed, and what you would like help with.'
                }
              />
            </label>
            <label className="care-full-width">
              Send to a reviewer
              <select name="reviewerId" value={draft.reviewerId} onChange={setField}>
                <option value="">Save without sending for review</option>
                {!reviewerValid && (
                  <option value={draft.reviewerId}>Previous reviewer — access has ended</option>
                )}
                {doctors.map((doctor) => (
                  <option key={doctor.personId} value={doctor.personId}>
                    {doctor.name}
                    {doctor.personId === record.primaryDoctorId ? ' · Primary reviewer' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="care-muted">
            {draft.reviewerId
              ? 'This update will be added to the selected doctor’s review queue. Ask your clinic about its response times. For urgent help, contact your clinic or local emergency service directly.'
              : 'This stays in the shared record, but no doctor will be assigned to review it.'}
          </p>
          {!stored && (
            <p className="care-error" role="status">
              This draft could not be kept in this tab. Keep this page open until you save it.
            </p>
          )}
          {(validation || error) && (
            <p className="care-error" role="alert">
              {validation || error}
            </p>
          )}
          <div className="care-actions">
            <button className="button" type="submit" disabled={saving}>
              {saving
                ? 'Saving…'
                : draft.reviewerId
                  ? 'Save & send for review'
                  : 'Save to timeline'}
            </button>
            <button
              className="button button--inverse"
              type="button"
              onClick={onClose}
              disabled={saving}
            >
              Back
            </button>
          </div>
        </fieldset>
      </form>
    </section>
  );
};
export default EntryComposer;
